import { useEffect, useState } from 'react';
import { useScrollLock } from './useScrollLock';

/**
 * Custom hook to detect and lock scroll when Featurebase feedback widget modal is open
 * Uses MutationObserver to watch for dynamically injected Featurebase modal
 *
 * Featurebase SDK has no close callback, so we must observe the DOM to detect
 * when the modal is opened/closed.
 */
export const useFeaturebaseScrollLock = () => {
  const [isFeaturebaseModalOpen, setIsFeaturebaseModalOpen] = useState(false);

  // Use the shared scroll lock hook
  useScrollLock(isFeaturebaseModalOpen);

  useEffect(() => {
    // Only run in browser
    if (typeof window === 'undefined') return;

    /**
     * Check if Featurebase modal is currently open
     * The Featurebase widget opens in an iframe that gets injected into the DOM
     */
    const checkFeaturebaseModal = () => {
      let isOpen = false;

      // Strategy 1: Check for Featurebase iframes with modal dimensions
      const featurebaseIframes = document.querySelectorAll('iframe[src*="featurebase"]');
      for (const iframe of featurebaseIframes) {
        const rect = iframe.getBoundingClientRect();
        const computedStyle = window.getComputedStyle(iframe);
        const parent = iframe.parentElement;
        const parentStyle = parent ? window.getComputedStyle(parent) : null;

        const isIframeVisible = computedStyle.display !== 'none' &&
                                computedStyle.visibility !== 'hidden' &&
                                parseFloat(computedStyle.opacity || '1') > 0;

        const isParentVisible = !parent || (
          parentStyle.display !== 'none' &&
          parentStyle.visibility !== 'hidden' &&
          parseFloat(parentStyle.opacity || '1') > 0
        );

        // Modal size threshold: wider than 250px and taller than 300px
        const hasModalSize = rect.width > 250 && rect.height > 300;

        if (isIframeVisible && isParentVisible && hasModalSize) {
          isOpen = true;
          break;
        }
      }

      // Strategy 2: Check for Featurebase modal container elements
      if (!isOpen) {
        const modalSelectors = [
          '[class*="featurebase"][class*="modal"]',
          '[id*="featurebase"][id*="modal"]',
          '[class*="fb-"][class*="modal"]',
          '[id*="fb-"][id*="modal"]',
          '[class*="featurebase-widget-modal"]',
          '[class*="fb-widget-modal"]',
        ];

        for (const selector of modalSelectors) {
          const elements = document.querySelectorAll(selector);
          for (const el of elements) {
            const rect = el.getBoundingClientRect();
            const computedStyle = window.getComputedStyle(el);

            const isVisible = computedStyle.display !== 'none' &&
                             computedStyle.visibility !== 'hidden' &&
                             parseFloat(computedStyle.opacity || '1') > 0;

            const hasModalSize = rect.width > 250 && rect.height > 300;

            if (isVisible && hasModalSize) {
              isOpen = true;
              break;
            }
          }
          if (isOpen) break;
        }
      }

      // Strategy 3: Check for overlay/backdrop elements
      // Key insight: Featurebase uses 'fb-feedback-widget-overlay' with/without '-hidden' class
      if (!isOpen) {
        const overlay = document.querySelector('.fb-feedback-widget-overlay');
        if (overlay) {
          // Check if overlay exists AND does NOT have the -hidden class
          const isHidden = overlay.classList.contains('fb-feedback-widget-overlay-hidden');
          if (!isHidden) {
            isOpen = true;
          }
        }
      }

      setIsFeaturebaseModalOpen(isOpen);
    };

    // Initial check
    checkFeaturebaseModal();

    // Create MutationObserver to watch for DOM changes
    const observer = new MutationObserver((mutations) => {
      // Check on ANY mutation that might affect Featurebase visibility
      // This includes class changes on the overlay element
      const hasRelevantMutation = mutations.some(mutation => {
        // Check attribute changes (like class changes on overlay)
        if (mutation.type === 'attributes' && mutation.target) {
          const element = mutation.target;
          // Use classList instead of className to avoid SVGAnimatedString issues
          // (SVG elements return an object for className, not a string)
          const classValue = element.classList?.value || '';
          if (classValue.includes('fb-') || classValue.includes('featurebase')) {
            return true;
          }
        }

        // Check if added/removed nodes contain featurebase-related elements
        const nodes = [...mutation.addedNodes, ...mutation.removedNodes];
        return nodes.some(node => {
          if (node.nodeType !== Node.ELEMENT_NODE) return false;
          const element = node;
          const className = element.className || '';
          // Check class names or tag names for Featurebase indicators
          return (
            className.includes?.('featurebase') ||
            className.includes?.('fb-') ||
            element.tagName === 'IFRAME' && element.src?.includes?.('featurebase')
          );
        });
      });

      if (hasRelevantMutation) {
        checkFeaturebaseModal();
      }
    });

    // Observe the entire document body for changes
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style'] // Watch for class/style changes
    });

    // Cleanup
    return () => {
      observer.disconnect();
    };
  }, [isFeaturebaseModalOpen]);
};
